import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";

type PrevLineItem = {
  id: number;
  name: string;
  is_used?: boolean;
};

function AreaTab() {
  const { control, setValue, watch, formState, getValues, trigger } = useFormContext();
  const { errors } = formState;

  const lineItems = watch("lineItems") || [];
  const prevlineItems: PrevLineItem[] = watch("prevlineItems") || [];
  const isAreaUsed = watch("is_used") === true;
  const [inputValue, setInputValue] = useState("");

  const isLineItemUsed = (itemName: string) =>
    prevlineItems.some(
      (item) => item.name === itemName && item.is_used === true
    );

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const trimmedValue = inputValue.trim();

      const currentLineItems = getValues("lineItems") || [];

      const alreadyExists = currentLineItems.some(
        (item: string) => item.toLowerCase() === trimmedValue.toLowerCase()
      );

      if (alreadyExists) {
        toast.error("This item already exists.");
        return;
      }

      const updatedItems = [...currentLineItems, trimmedValue];
      setValue("lineItems", updatedItems, { shouldDirty: true });
      setInputValue("");

      await trigger("lineItems");
    }
  };

  const handleDelete = (itemToDelete: string) => {
    if (isLineItemUsed(itemToDelete)) {
      return;
    }

    const filtered = lineItems.filter((item: string) => item !== itemToDelete);
    setValue("lineItems", filtered, { shouldDirty: true });
  };

  return (
    <div>
      <Tooltip
        title={
          isAreaUsed
            ? "This area is linked to an application so its name can't be changed."
            : ""
        }
        disableHoverListener={!isAreaUsed}
        disableFocusListener={!isAreaUsed}
        arrow
      >
        <span className="block w-full">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                className="mt-2 mb-4"
                required
                label="Name"
                autoFocus={!isAreaUsed}
                id="name"
                variant="outlined"
                fullWidth
                disabled={isAreaUsed}
                error={!!errors.name}
                helperText={errors?.name?.message as string}
              />
            )}
          />
        </span>
      </Tooltip>

      <TextField
        label="Line Items"
        variant="outlined"
        fullWidth
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="mb-4"
        error={!!errors.lineItems}
        helperText={
          (errors.lineItems?.message as string) ??
          "Press Enter to add a line item"
        }
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {lineItems.map((item: string, index: number) => {
          const itemIsUsed = isLineItemUsed(item);

          const chip = (
            <Chip
              key={index}
              label={item}
              disabled={itemIsUsed}
              onDelete={itemIsUsed ? undefined : () => handleDelete(item)}
            />
          );

          if (!itemIsUsed) {
            return chip;
          }

          return (
            <Tooltip
              key={index}
              title="This line item is used in an application so it can't be removed."
              arrow
            >
              <span>{chip}</span>
            </Tooltip>
          );
        })}
      </Box>
    </div>
  );
}

export default AreaTab;
